import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Linkedin, Twitter, Facebook, Youtube, Mail, X } from 'lucide-react';
import { useBrandSettings } from '../contexts/BrandSettingsContext';
import BrandLogo from './BrandLogo';
import { Button } from './ui/button';
import MobileMenu, { HamburgerIcon } from './MobileMenu';

const SiteLayout = ({ children }) => {
  const {
    brandName,
    brandTagline,
    logoUrl,
    topbar,
    header,
    footer,
    mainNavLinks,
    footerLinks,
    loading: settingsLoading
  } = useBrandSettings();

  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [topbarDismissed, setTopbarDismissed] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('topbar_dismissed');
    if (dismissed === 'true') {
      setTopbarDismissed(true);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const threshold = parseInt(header?.scrollThreshold) || 50;
      setIsScrolled(window.scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [header?.scrollThreshold]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu and scroll to top on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // CMS-driven header styling - no fallbacks, values come from validated CMS settings
  const headerBg = header?.isTransparent && !isScrolled
    ? (header?.transparentBgColor || 'transparent')
    : (isScrolled ? (header?.bgColorScrolled || header?.bgColor) : header?.bgColor);

  const headerTextColor = header?.isTransparent && !isScrolled
    ? '#ffffff'
    : (isScrolled ? (header?.textColorScrolled || header?.textColor) : header?.textColor);

  const headerHeight = isScrolled
    ? `${header?.heightScrolled || header?.height}px`
    : `${header?.height}px`;

  const headerStyles = {
    backgroundColor: headerBg,
    color: headerTextColor,
    height: headerHeight,
    transition: 'all 0.3s ease',
    boxShadow: isScrolled && header?.showShadowOnScroll !== false ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
    borderBottom: header?.borderEnabled !== false ? `${header?.borderWidth || 1}px solid ${header?.borderColor}` : 'none'
  };

  // CMS-driven footer styling - no fallbacks, values come from validated CMS settings
  const footerStyles = {
    backgroundColor: footer?.bgColor,
    color: footer?.textColor,
    backgroundImage: footer?.backgroundImage
      ? `${footer.backgroundOverlay ? `linear-gradient(${footer.backgroundOverlay}, ${footer.backgroundOverlay}), ` : ''}url(${footer.backgroundImage})`
      : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  const groupedFooterLinks = React.useMemo(() => {
    const groups = {};
    (footerLinks || []).forEach((link) => {
      const section = link.section || 'General';
      if (!groups[section]) groups[section] = [];
      groups[section].push(link);
    });
    return groups;
  }, [footerLinks]);

  const renderNavLink = (link, index) => {
    const href = link.href;
    const hasChildren = link.children && link.children.length > 0;
    const linkStyle = { color: header?.linkColor };
    const hoverColor = header?.linkHoverColor;

    if (hasChildren) {
      return (
        <div key={link.id || index} className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === link.id ? null : link.id)}
            className="flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary"
            style={linkStyle}
          >
            {link.label}
            <ChevronDown size={14} className={`transition-transform ${openDropdown === link.id ? 'rotate-180' : ''}`} />
          </button>

          {openDropdown === link.id && (
            <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
              {link.children.map((child, idx) => (
                <Link
                  key={child.id || idx}
                  to={child.href}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                  onClick={() => setOpenDropdown(null)}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (href.startsWith('#') || href.startsWith('/#')) {
      return (
        <a
          key={link.id || href}
          href={href}
          className="text-sm font-medium transition-colors hover:text-primary"
          style={linkStyle}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        key={link.id || href}
        to={href}
        className="text-sm font-medium transition-colors hover:text-primary"
        style={linkStyle}
      >
        {link.label}
      </Link>
    );
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'linkedin': return <Linkedin size={20} />;
      case 'twitter': return <Twitter size={20} />;
      case 'facebook': return <Facebook size={20} />;
      case 'youtube': return <Youtube size={20} />;
      default: return <Mail size={20} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Topbar */}
      {topbar?.enabled && !topbarDismissed && (
        <div 
          className="py-2 px-4 text-center text-sm"
          style={{ backgroundColor: topbar.bgColor, color: topbar.textColor }}
        >
          <span>{topbar.text}</span>
          {topbar.linkLabel && topbar.linkHref && (
            <Link to={topbar.linkHref} className="ml-2 underline font-medium">
              {topbar.linkLabel}
            </Link>
          )}
          <button 
            onClick={() => {
              setTopbarDismissed(true);
              sessionStorage.setItem('topbar_dismissed', 'true');
            }}
            className="ml-4 opacity-70 hover:opacity-100"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <header 
        className={`${header?.sticky !== false ? 'sticky top-0' : ''} z-40`}
        style={headerStyles}
      >
        <div className="container-wide h-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <BrandLogo 
              variant={header?.isTransparent && !isScrolled ? 'transparent' : 'default'} 
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {(mainNavLinks || []).map((link, index) => renderNavLink(link, index))}
          </nav>

          {/* Actions - Driven by CMS via BrandSettingsContext */}
          <div className="hidden lg:flex items-center gap-4">
            {(header?.actions || []).map((action) => {
              const isExternal = action.href?.startsWith('http');
              
              if (action.variant === 'primary') {
                if (isExternal) {
                  return (
                    <a key={action.id} href={action.href} target="_blank" rel="noopener noreferrer">
                      <Button>{action.label}</Button>
                    </a>
                  );
                }
                return (
                  <Link key={action.id} to={action.href}>
                    <Button>{action.label}</Button>
                  </Link>
                );
              }
              
              if (isExternal) {
                return (
                  <a 
                    key={action.id} 
                    href={action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm font-medium hover:text-primary transition-colors ${action.className || ''}`}
                    style={{ color: header?.linkColor }}
                  >
                    {action.label}
                  </a>
                );
              }
              
              return (
                <Link 
                  key={action.id} 
                  to={action.href}
                  className={`text-sm font-medium hover:text-primary transition-colors ${action.className || ''}`}
                  style={{ color: header?.linkColor }}
                >
                  {action.label}
                </Link>
              );
            })}
          </div>

          {/* Mobile Menu Button - Modern Hamburger */}
          <div className="lg:hidden">
            <HamburgerIcon
              isOpen={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              color={headerTextColor}
              size={parseInt(header?.mobileMenu?.hamburgerSize) || 24}
              lineWidth={parseInt(header?.mobileMenu?.hamburgerLineWidth) || 2}
              style={header?.mobileMenu?.hamburgerStyle || 'default'}
            />
          </div>
        </div>
      </header>

      {/* Modern Mobile Menu - Driven by CMS via BrandSettingsContext */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navLinks={mainNavLinks || []}
        actions={header?.actions || []}
        settings={header?.mobileMenu || {}}
        brandName={brandName}
      />

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer style={footerStyles}>
        <div className="w-[80%] mx-auto py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              {footer?.showLogo !== false && (
                <BrandLogo variant="light" className="mb-4" />
              )}
              {footer?.showTagline !== false && (
                <p className="text-gray-400 mb-6 max-w-sm">
                  {brandTagline}
                </p>
              )}
              {footer?.showSocial !== false && (
                <div className="flex gap-4">
                  {(footer?.socialLinks || []).filter(s => s.url).map((social) => (
                    <a 
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {getSocialIcon(social.platform)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Link Columns */}
            {Object.entries(groupedFooterLinks).map(([section, links]) => (
              <div key={section}>
                <h4
                  className="font-semibold mb-4"
                  style={{ color: footer?.headingColor }}
                >
                  {section}
                </h4>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.id}>
                      <Link
                        to={link.href}
                        className="text-sm transition-colors"
                        style={{ color: footer?.linkColor }}
                        onMouseEnter={(e) => e.target.style.color = footer?.linkHoverColor}
                        onMouseLeave={(e) => e.target.style.color = footer?.linkColor}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Copyright & Powered By */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Copyright - CMS controlled, no fallback */}
              {footer?.copyrightText && (
                <div className="text-sm text-gray-500">
                  {footer.copyrightText
                    .replace('{year}', new Date().getFullYear())
                    .replace('{brandName}', brandName)}
                </div>
              )}

              {/* Powered By - CMS controlled, no fallback */}
              {footer?.showPoweredBy !== false && footer?.poweredByText && (
                <div className="text-sm">
                  {footer?.poweredByLink ? (
                    <a
                      href={footer.poweredByLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                    >
                      {footer?.poweredByLogoLightUrl ? (
                        <>
                          <img
                            src={footer.poweredByLogoLightUrl}
                            alt={footer.poweredByText}
                            className={`h-5 object-contain transition-opacity ${footer.poweredByLogoLightHoverUrl ? 'group-hover:hidden' : 'group-hover:opacity-80'}`}
                          />
                          {footer.poweredByLogoLightHoverUrl && (
                            <img
                              src={footer.poweredByLogoLightHoverUrl}
                              alt={footer.poweredByText}
                              className="h-5 object-contain hidden group-hover:block"
                            />
                          )}
                        </>
                      ) : (
                        <span>{footer.poweredByText}</span>
                      )}
                    </a>
                  ) : (
                    <div className="inline-flex items-center gap-2 text-gray-400">
                      {footer?.poweredByLogoLightUrl ? (
                        <img
                          src={footer.poweredByLogoLightUrl}
                          alt={footer.poweredByText}
                          className="h-5 object-contain"
                        />
                      ) : (
                        <span>{footer.poweredByText}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiteLayout;
